import Swiper from 'swiper'
import { Navigation, Pagination, Autoplay, Scrollbar } from 'swiper/modules'
import breakpoints from '@config/breakpoints.json'
import { inTestEnvironment } from 'shared/javascripts/util/environment'
import { trackEvent } from '../util/analytics'

// Initializes a Swiper slider on the given element.
// https://idangero.us/swiper/
//
// If there is only 1 slide, slider initialization is skipped. DOM is still prepared to ensure proper styling.
//
const MAX_SLIDES_FOR_BULLET_PAGINATION = 6
const SECONDS_PER_SLIDE = 7 * 1000

interface SliderData {
  autoplay?: boolean
  slidesPerViewXS?: number
  slidesPerViewSM?: number
  slidesPerViewMD?: number
  slidesPerViewLG?: number
  slidesPerViewXL?: number
  centeredSlides?: boolean
  centeredSlidesBounds?: boolean
  grouped?: boolean
  loop?: boolean
  spaceBetween?: number
  watchSlidesProgress?: boolean
  scrollbar?: boolean
  insidePagination?: boolean
  autoFraction?: boolean
  previousLabel?: string
  nextLabel?: string
  autoActivate?: boolean
}

interface SliderElement extends HTMLElement {
  slider: Slider
}

class Slider {
  private element: HTMLElement
  private autoplay: boolean
  private slidesPerViewXS: number
  private slidesPerViewSM: number
  private slidesPerViewMD: number
  private slidesPerViewLG: number
  private slidesPerViewXL: number
  private centeredSlides: boolean
  private centeredSlidesBounds: boolean
  private grouped: boolean
  private loop: boolean | undefined
  private spaceBetween: number
  private watchSlidesProgress: boolean
  private scrollbar: boolean
  private insidePagination: boolean
  private autoFraction: boolean
  private slides: NodeListOf<Element>
  private swiper: Swiper | null
  private pagination!: HTMLElement
  private previousLabel: string
  private nextLabel: string

  constructor(element: HTMLElement, data: SliderData) {
    this.element = element
    this.autoplay = data?.autoplay ?? true
    this.slidesPerViewXS = Number(data?.slidesPerViewXS ?? 1) // Everything smaller than SM
    this.slidesPerViewSM = Number(data?.slidesPerViewSM ?? 1)
    this.slidesPerViewMD = Number(data?.slidesPerViewMD ?? 1)
    this.slidesPerViewLG = Number(data?.slidesPerViewLG ?? 1)
    this.slidesPerViewXL = Number(data?.slidesPerViewXL ?? 1)
    this.centeredSlides = data?.centeredSlides ?? false
    this.centeredSlidesBounds = data?.centeredSlidesBounds ?? false
    this.grouped = data?.grouped ?? false
    this.loop = data?.loop
    this.spaceBetween = Number(data?.spaceBetween ?? 0)
    this.watchSlidesProgress = data?.watchSlidesProgress ?? false
    this.scrollbar = data?.scrollbar ?? false
    this.insidePagination = data?.insidePagination ?? true
    this.autoFraction = data?.autoFraction ?? true
    this.slides = this.element.querySelectorAll('.slider--slide')
    this.swiper = null
    this.previousLabel = data?.previousLabel ?? 'Zurück'
    this.nextLabel = data?.nextLabel ?? 'Weiter'

    if (inTestEnvironment()) {
      this.autoplay = false
    }
  }

  activate(): (() => void) | undefined {
    this.slides.forEach((slide) => {
      slide.classList.add('swiper-slide')
      slide.remove()
    })

    const slideWrapper = document.createElement('div')
    slideWrapper.classList.add('swiper-wrapper')
    this.slides.forEach((slide) => {
      slideWrapper.appendChild(slide)
    })

    const swiperRoot = document.createElement('div')
    swiperRoot.classList.add('swiper')
    swiperRoot.appendChild(slideWrapper)

    let scrollbar: HTMLElement | undefined
    if (this.scrollbar) {
      scrollbar = document.createElement('div')
      scrollbar.classList.add('slider--scrollbar', 'swiper-scrollbar')
      swiperRoot.appendChild(scrollbar)
    }

    const rootWrapper = document.createElement('div')
    rootWrapper.classList.add('slider--root-wrapper')
    rootWrapper.appendChild(swiperRoot)

    this.element.appendChild(rootWrapper)
    this.element.classList.add('-ready')

    if (this.slides) {
      const pagination = document.createElement('div')
      pagination.classList.add('slider--pagination')
      this.pagination = pagination
      pagination.classList.toggle('-inside', this.insidePagination)

      const previous = up.element.createFromHTML(
        '<button type="button" class="slider--previous">',
      ) as HTMLButtonElement
      previous.ariaLabel = this.previousLabel
      const next = up.element.createFromHTML(
        '<button type="button" class="slider--next">',
      ) as HTMLButtonElement
      next.ariaLabel = this.nextLabel

      this.element.append(pagination, previous, next)

      const swiperOptions: Record<string, any> = {
        direction: 'horizontal',
        autoplay: this.autoplay && { delay: SECONDS_PER_SLIDE },
        centeredSlides: this.centeredSlides,
        centeredSlidesBounds: this.centeredSlidesBounds,
        slidesPerView: this.slidesPerViewXS,
        spaceBetween: this.spaceBetween,
        watchSlidesProgress: this.watchSlidesProgress,
        loopFillGroupWithBlank: true,
        modules: [Navigation, Pagination, Autoplay, ...(this.scrollbar ? [Scrollbar] : [])],
        pagination: {
          el: pagination,
          bulletClass: 'slider--pagination-bullet',
          bulletActiveClass: '-active',
          clickable: true,
          clickableClass: '-clickable',
        },
        breakpoints: {
          [breakpoints.sm]: {},
          [breakpoints.md]: {},
          [breakpoints.lg]: {},
          [breakpoints.xl]: {},
        },
        navigation: {
          prevEl: previous,
          nextEl: next,
          disabledClass: '-disabled',
        },
        scrollbar: scrollbar && {
          el: scrollbar,
          draggable: true,
        },
        on: {
          slideChangeTransitionEnd: (event: Swiper) => {
            this.#syncActiveSlideIndex(event)
            this.#syncFullyVisibleSlides()
          },
          afterInit: (event: Swiper) => {
            this.#syncActiveSlideIndex(event)
            this.#syncFullyVisibleSlides()
          },
          setTranslate: () => this.#syncFullyVisibleSlides(),
          sliderMove: () => this.#syncFullyVisibleSlides(),
          scrollbarDragMove: () => this.#syncFullyVisibleSlides(),
          transitionEnd: () => this.#syncFullyVisibleSlides(),
          resize: () => this.#syncFullyVisibleSlides(),
        },
      }

      this.assignViewportOptions(swiperOptions, this.slidesPerViewXS)

      this.assignViewportOptions(swiperOptions.breakpoints[breakpoints.sm], this.slidesPerViewSM)
      this.assignViewportOptions(swiperOptions.breakpoints[breakpoints.md], this.slidesPerViewMD)
      this.assignViewportOptions(swiperOptions.breakpoints[breakpoints.lg], this.slidesPerViewLG)
      this.assignViewportOptions(swiperOptions.breakpoints[breakpoints.xl], this.slidesPerViewXL)

      const swiper = new Swiper(swiperRoot, swiperOptions)
      this.swiper = swiper

      swiper.on('touchEnd', () => {
        this.trackInteraction('slider.dragSlide')
      })
      next.addEventListener('click', () => {
        this.trackInteraction('slider.clickNext')
      })
      previous.addEventListener('click', () => {
        this.trackInteraction('slider.clickPrevious')
      })
      pagination.addEventListener('click', () => {
        this.trackInteraction('slider.clickPagination')
      })

      return () => {
        this.destroy()
      }
    }
  }

  destroy(): void {
    if (this.swiper !== undefined) {
      this.pagination.remove()
      this.swiper!.destroy()
    }
  }

  assignViewportOptions(options: Record<string, any>, slidesPerView: number): void {
    options.enabled = this.slides.length > slidesPerView
    options.slidesPerView = slidesPerView
    options.loop = this.loop ?? this.hasEnoughSlidesToLoop(slidesPerView)
    if (this.grouped) {
      options.slidesPerGroup = slidesPerView
    }

    const numberOfSlides = this.slides.length
    const numberOfBullets = this.grouped ? numberOfSlides / slidesPerView : numberOfSlides

    if (this.autoFraction && numberOfBullets > MAX_SLIDES_FOR_BULLET_PAGINATION) {
      options.pagination = options.pagination || {}
      options.pagination.type = 'fraction'
    }
  }

  hasEnoughSlidesToLoop(slidesPerView: number): boolean {
    return this.slides.length >= slidesPerView + (this.grouped ? slidesPerView : 1)
  }

  trackInteraction(eventName: string): void {
    const numberOfSlides = this.slides.length
    const currentSlide = this.swiper!.realIndex + 1 // activeIndex is sometimes something else; previousIndex is mostly messed up
    const nthInPage = [...document.querySelectorAll('[data-slider]')].indexOf(this.element) + 1

    trackEvent(eventName, { slider: { nthInPage, numberOfSlides, currentSlide } })
  }

  #syncActiveSlideIndex(indexed: Swiper): void {
    this.element.dataset.activeSlideIndex = String(indexed.realIndex) // as mentioned above, activeIndex is sometimes broken
  }

  #syncFullyVisibleSlides(): void {
    if (!this.watchSlidesProgress) return

    const visibleBounds = this.element
      .querySelector('.slider--root-wrapper')
      ?.getBoundingClientRect()
    if (!visibleBounds) return

    const tolerance = 1 // px
    this.slides.forEach((slide) => {
      const slideBounds = slide.getBoundingClientRect()
      const fullyVisible =
        slideBounds.left >= visibleBounds.left - tolerance &&
        slideBounds.right <= visibleBounds.right + tolerance

      slide.classList.toggle('-fully-visible', fullyVisible)
    })
  }
}

up.compiler('[data-slider]', (element: HTMLElement, data: SliderData) => {
  const autoActivate = 'autoActivate' in data ? data.autoActivate : true

  ;(element as SliderElement).slider = new Slider(element, data)
  if (autoActivate) {
    ;(element as SliderElement).slider.activate()
  }
})
